<template>
  <el-dialog title="采购订单行追溯" :visible.sync="visibleInner" width="900px" append-to-body @close="handleClose">
    <div v-if="head" class="trace-head">
      <span>{{ head.orderCode }}</span>
      <span class="sep">|</span>
      <span>{{ head.materialCode }} {{ head.materialName }}</span>
      <span class="sep">|</span>
      <span>订单量 {{ head.orderQuantity }}</span>
      <span class="sep">|</span>
      <span>{{ head.partnerName }}</span>
    </div>
    <el-table :data="nodes" border v-loading="loading" max-height="420" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
      <el-table-column type="index" label="#" width="45" align="center" />
      <el-table-column prop="nodeType" label="类型" width="100">
        <template slot-scope="scope">
          <el-tag :type="tagType(scope.row.nodeType)" size="mini">{{ typeLabel(scope.row.nodeType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="billCode" label="单号" min-width="140">
        <template slot-scope="scope">
          <a v-if="canOpen(scope.row)" class="code-link" @click="openDoc(scope.row)">{{ scope.row.billCode }}</a>
          <span v-else>{{ scope.row.billCode }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="billDate" label="日期" width="110" align="center">
        <template slot-scope="scope">{{ formatDate(scope.row.billDate) }}</template>
      </el-table-column>
      <el-table-column prop="approveStatus" label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.approveStatus === '2'" type="success" size="mini">已审</el-tag>
          <el-tag v-else type="info" size="mini">未审</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="quantity" label="数量" width="90" align="right" />
      <el-table-column prop="amount" label="金额" width="100" align="right" />
      <el-table-column prop="extraInfo" label="备注" min-width="100" show-overflow-tooltip />
    </el-table>
    <div v-if="!loading && (!nodes || !nodes.length)" class="empty-tip">暂无下游单据</div>
    <span slot="footer"><el-button @click="visibleInner = false">关闭</el-button></span>
    <div v-show="false">
      <component :is="pageComp" v-if="pageComp" ref="page" />
    </div>
  </el-dialog>
</template>

<script>
import { docGet } from '../../api/erp/doc'
import { fetchPerms } from '../../api/auth'

const TYPE_LABELS = {
  arrival: '到货通知', inspection: '来料检验', stockIn: '采购入库', return: '采购退货', stockReturn: '退货出库',
  payable: '应付单', invoice: '进项发票', payment: '付款', returnSettle: '退货结算',
  invoiceRed: '进项红字', paymentRed: '付款红冲'
}

const DOCS = {
  arrival: { perm: 'purchase:purchaseArrivalNotice:info', load: () => import('../../views/purchase/purchaseArrivalNotice.vue') },
  inspection: { perm: 'purchase:purchaseIncomingInspection:info', load: () => import('../../views/purchase/purchaseIncomingInspection.vue') },
  stockIn: { perm: 'purchase:purchaseStockIn:info', load: () => import('../../views/purchase/purchaseStockIn.vue') },
  return: { perm: 'purchase:purchaseReturn:info', load: () => import('../../views/purchase/purchaseReturn.vue') },
  stockReturn: { perm: 'purchase:purchaseStockReturn:info', load: () => import('../../views/purchase/purchaseStockReturn.vue') },
  payable: { perm: 'finance:payable:info', load: () => import('../../views/finance/Payable.vue') },
  invoice: { perm: 'finance:invoicePurchase:info', load: () => import('../../views/finance/InvoicePurchase.vue') },
  payment: { perm: 'finance:payment:info', load: () => import('../../views/finance/Payment.vue') },
  returnSettle: { perm: 'finance:purchaseReturnSettle:info', load: () => import('../../views/finance/PurchaseReturnSettle.vue') },
  invoiceRed: { perm: 'finance:invoicePurchaseRed:info', load: () => import('../../views/finance/InvoicePurchaseRed.vue') },
  paymentRed: { perm: 'finance:paymentRed:info', load: () => import('../../views/finance/PaymentRed.vue') }
}

export default {
  props: {
    visible: { type: Boolean, default: false },
    orderLineId: { type: String, default: '' }
  },
  data() { return { visibleInner: false, loading: false, head: null, nodes: [], pageComp: null, permCodes: [] } },
  created() { fetchPerms().then(res => { this.permCodes = res.data || [] }).catch(() => {}) },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.visibleInner = !!val
        if (val && this.orderLineId) this.loadTrace()
      }
    },
    orderLineId(val) {
      if (this.visibleInner && val) this.loadTrace()
    }
  },
  methods: {
    isAuth(perm) {
      const user = this.$store.state.user || {}
      const codes = Array.isArray(user.roleCodes) ? user.roleCodes : []
      if (['TENANT_ADMIN', 'PLATFORM_ADMIN'].indexOf(String(user.roleCode || '')) >= 0 || codes.indexOf('TENANT_ADMIN') >= 0 || codes.indexOf('PLATFORM_ADMIN') >= 0) return true
      return this.permCodes.indexOf(perm) >= 0
    },
    loadTrace() {
      this.loading = true
      this.head = null
      this.nodes = []
      docGet('/purchase/purchaseOrderExecution/trace/' + this.orderLineId).then(res => {
        this.head = res.data || null
        this.nodes = (res.data && res.data.nodes) || []
      }).catch(err => {
        this.head = null
        this.nodes = []
        this.$message.error((err && err.message) || '加载采购追溯失败')
      }).finally(() => { this.loading = false })
    },
    canOpen(row) {
      const cfg = DOCS[row.nodeType]
      return !!(row && row.billId && cfg && this.isAuth(cfg.perm))
    },
    openDoc(row) {
      const cfg = DOCS[row.nodeType]
      if (!row.billId || !cfg) return
      if (!this.isAuth(cfg.perm)) { this.$message.warning('无查看该单据的权限'); return }
      cfg.load().then(mod => {
        this.pageComp = mod.default
        this.$nextTick(() => {
          const page = this.$refs.page
          if (!page) return
          if (typeof page.open === 'function') page.open(row.billId, true)
          else if (typeof page.addOrUpdateHandle === 'function') page.addOrUpdateHandle(row.billId, true)
        })
      }).catch(() => this.$message.error('打开单据详情失败'))
    },
    typeLabel(t) { return TYPE_LABELS[t] || t },
    tagType(t) {
      if (t === 'invoice' || t === 'payment') return 'success'
      if (t === 'payable') return ''
      return 'info'
    },
    formatDate(d) { return d ? String(d).slice(0, 10) : '' },
    handleClose() { this.$emit('update:visible', false) }
  }
}
</script>

<style scoped>
.trace-head { margin-bottom: 12px; color: #606266; font-size: 13px; }
.trace-head .sep { margin: 0 8px; color: #dcdfe6; }
.empty-tip { text-align: center; color: #909399; padding: 24px 0; }
.code-link { color: #348adb; cursor: pointer; }
</style>
